import React, { useEffect } from "react";
import {
  Box, Typography, Card, CardContent, Avatar, Chip,
  CircularProgress, Alert, List, ListItem, ListItemText,
  ListItemAvatar, Divider, IconButton, useTheme, useMediaQuery,
} from "@mui/material";
import {
  People, TrendingUp, Payment, AccountBalance,
  Today, ViewWeek, CalendarMonth, AllInclusive, AttachMoney,
  Casino, MeetingRoom, Refresh, SportsEsports, ShoppingCart,
} from "@mui/icons-material";
import {
  ResponsiveContainer, BarChart, Bar,
  PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend,
} from "recharts";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import { fetchDashboardReport, setPeriod } from "../store/slices/dashboardSlice";
import dayjs from "dayjs";
import GiftBonusPanel from "../components/dashboard/GiftBonusPanel";

// ─── Formatters & Constants ──────────────────────────────────────────────────
const fmt = (n: number) =>
  new Intl.NumberFormat("en-ET", { style: "currency", currency: "ETB" }).format(n);

const COLORS = ["#6366f1", "#ec4899", "#06b6d4", "#10b981", "#f59e0b", "#8b5cf6"];
const RANK_COLORS = ["#f59e0b", "#94a3b8", "#b45309", "#6366f1", "#8b5cf6"];

// ─── Stat Card Component (2-Column Mobile First) ─────────────────────────────
interface StatCardProps {
  title: string;
  value: string;
  sub: string;
  gradient: string;
  icon: React.ReactNode;
}

const StatCard: React.FC<StatCardProps> = ({ title, value, sub, gradient, icon }) => (
  <Card
    sx={{
      background: gradient,
      color: "white",
      position: "relative",
      overflow: "hidden",
      borderRadius: { xs: 2.5, sm: 3 },
      boxShadow: "0 4px 14px rgba(0, 0, 0, 0.22)",
      display: "flex",
      flexDirection: "column",
      justifyContent: "space-between",
      transition: "transform 0.15s ease",
      "&:active": { transform: "scale(0.98)" },
    }}
  >
    <CardContent
      sx={{
        p: { xs: 1.5, sm: 2 },
        "&:last-child": { pb: { xs: 1.5, sm: 2 } },
        position: "relative",
        zIndex: 2,
      }}
    >
      <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={0.75}>
        <Typography
          variant="caption"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "0.7rem", sm: "0.8rem" },
            letterSpacing: 0.4,
            opacity: 0.92,
            textTransform: "uppercase",
            lineHeight: 1.2,
          }}
        >
          {title}
        </Typography>
        <Avatar
          sx={{
            bgcolor: "rgba(255, 255, 255, 0.2)",
            width: { xs: 32, sm: 38 },
            height: { xs: 32, sm: 38 },
            "& .MuiSvgIcon-root": { fontSize: { xs: "1.1rem", sm: "1.35rem" } },
            flexShrink: 0,
            ml: 0.5,
          }}
        >
          {icon}
        </Avatar>
      </Box>

      <Typography
        variant="h6"
        sx={{
          fontWeight: 800,
          fontSize: { xs: "1.05rem", sm: "1.35rem", md: "1.5rem" },
          lineHeight: 1.2,
          mb: 0.25,
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {value}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          opacity: 0.85,
          fontSize: { xs: "0.65rem", sm: "0.72rem" },
          display: "block",
          whiteSpace: "nowrap",
          overflow: "hidden",
          textOverflow: "ellipsis",
        }}
      >
        {sub}
      </Typography>
    </CardContent>

    <Box
      sx={{
        position: "absolute",
        top: -16,
        right: -16,
        width: { xs: 60, sm: 75 },
        height: { xs: 60, sm: 75 },
        borderRadius: "50%",
        background: "rgba(255, 255, 255, 0.08)",
        zIndex: 1,
        pointerEvents: "none",
      }}
    />
  </Card>
);

// ─── Section Title ────────────────────────────────────────────────────────────
const SectionTitle: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Typography
    variant="h6"
    sx={{
      fontWeight: 800,
      mb: 1.5,
      mt: 2.5,
      fontSize: { xs: "0.95rem", sm: "1.1rem" },
      letterSpacing: 0.2,
      display: "flex",
      alignItems: "center",
      gap: 1,
    }}
  >
    {children}
  </Typography>
);

// ─── Main Component ───────────────────────────────────────────────────────────
const DashboardPage: React.FC = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { report, period, loading, error } = useSelector((state: RootState) => state.dashboard);

  useEffect(() => {
    dispatch(fetchDashboardReport(period) as any);
  }, [dispatch, period]);

  const handlePeriod = (p: "daily" | "weekly" | "monthly" | "yearly" | "all") => {
    dispatch(setPeriod(p));
    dispatch(fetchDashboardReport(p) as any);
  };

  const handleRefresh = () => {
    dispatch(fetchDashboardReport(period) as any);
  };

  const totalDeposits = report?.statistics?.deposits?.totalAmount || report?.overview?.totalDeposits || 0;
  const totalWithdrawals = report?.statistics?.withdrawals?.totalAmount || report?.overview?.totalWithdrawals || 0;
  const netFlow = totalDeposits - totalWithdrawals;

  // Chart data
  const financialBarData = [
    { name: "Deposits", value: totalDeposits },
    { name: "Withdrawals", value: totalWithdrawals },
    { name: "Net Flow", value: Math.abs(netFlow) },
    { name: "House Income", value: report?.houseFeeIncome?.totalIncome || 0 },
  ];

  const depositStatusData = [
    { name: "Completed", value: report?.statistics?.deposits?.statusBreakdown?.completed?.count || 0 },
    { name: "Pending", value: report?.statistics?.deposits?.statusBreakdown?.pending?.count || 0 },
    { name: "Failed", value: report?.statistics?.deposits?.statusBreakdown?.failed?.count || 0 },
  ].filter((d) => d.value > 0);

  const withdrawalStatusData = [
    { name: "Approved", value: report?.statistics?.withdrawals?.statusBreakdown?.approved?.count || 0 },
    { name: "Pending", value: report?.statistics?.withdrawals?.statusBreakdown?.pending?.count || 0 },
    { name: "Rejected", value: report?.statistics?.withdrawals?.statusBreakdown?.rejected?.count || 0 },
    { name: "Completed", value: report?.statistics?.withdrawals?.statusBreakdown?.completed?.count || 0 },
  ].filter((d) => d.value > 0);

  const userStatsData = [
    { name: "Total", value: report?.statistics?.users?.totalUsers || report?.userStats?.totalUsers || 0 },
    { name: "Active", value: report?.statistics?.users?.activeUsers || report?.userStats?.activeUsers || 0 },
    { name: "Blocked", value: report?.statistics?.users?.blockedUsers || report?.userStats?.blockedUsers || 0 },
    { name: "New", value: report?.statistics?.users?.newUsers || report?.userStats?.newUsers || report?.today?.newUsers || 0 },
  ];

  const roomTournamentData = [
    { name: "Rooms", value: report?.statistics?.rooms?.totalRooms || 0 },
    { name: "Active", value: report?.statistics?.rooms?.activeRooms || report?.gameStats?.activeRooms || 0 },
    { name: "Tournaments", value: report?.statistics?.tournaments?.totalTournaments || 0 },
    { name: "Cards Sold", value: report?.gameStats?.soldCards || 0 },
  ];

  const txTypeData = (report?.statistics?.transactions?.stats || []).map((s: any) => ({
    name: s._id,
    count: s.count,
    amount: s.totalAmount,
  }));

  const houseBreakdownData = report?.houseFeeIncome?.breakdown
    ? Object.entries(report.houseFeeIncome.breakdown).map(([k, v]: [string, any]) => ({ name: k, value: v.total }))
    : [];

  const PERIODS: Array<{ key: "daily" | "weekly" | "monthly" | "yearly" | "all"; label: string; icon: React.ReactNode }> = [
    { key: "daily", label: "Daily", icon: <Today fontSize="small" /> },
    { key: "weekly", label: "Weekly", icon: <ViewWeek fontSize="small" /> },
    { key: "monthly", label: "Monthly", icon: <CalendarMonth fontSize="small" /> },
    { key: "yearly", label: "Yearly", icon: <CalendarMonth fontSize="small" /> },
    { key: "all", label: "All-Time", icon: <AllInclusive fontSize="small" /> },
  ];

  return (
    <Box sx={{ width: "100%", overflowX: "hidden" }}>
      {/* ── Header & Period Control ── */}
      <Box sx={{ mb: 2.5 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 800, fontSize: { xs: "1.35rem", sm: "1.75rem" } }}>
              Dashboard
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.25, fontSize: "0.75rem" }}>
              {report?.reportPeriod?.period
                ? `${report.reportPeriod.period.toUpperCase()} · ${dayjs(report.reportPeriod.startDate).format("MMM DD")} – ${dayjs(report.reportPeriod.endDate).format("MMM DD, YYYY")}`
                : "Real-time Platform Overview"}
            </Typography>
          </Box>

          <IconButton
            size="small"
            onClick={handleRefresh}
            disabled={loading}
            sx={{
              bgcolor: "rgba(255, 255, 255, 0.05)",
              border: "1px solid rgba(255, 255, 255, 0.1)",
              p: 1,
            }}
          >
            <Refresh
              fontSize="small"
              sx={{
                animation: loading ? "spin 1s linear infinite" : "none",
                "@keyframes spin": {
                  "0%": { transform: "rotate(0deg)" },
                  "100%": { transform: "rotate(360deg)" },
                },
              }}
            />
          </IconButton>
        </Box>

        {/* Horizontal Swipeable Period Selector */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            overflowX: "auto",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            "&::-webkit-scrollbar": { display: "none" },
            py: 0.5,
            px: 0.25,
            mt: 1,
          }}
        >
          {PERIODS.map((p) => {
            const active = period === p.key;
            return (
              <Chip
                key={p.key}
                icon={p.icon as React.ReactElement}
                label={p.label}
                clickable
                onClick={() => handlePeriod(p.key)}
                color={active ? "primary" : "default"}
                variant={active ? "filled" : "outlined"}
                sx={{
                  fontWeight: active ? 700 : 500,
                  fontSize: { xs: "0.75rem", sm: "0.82rem" },
                  height: { xs: 32, sm: 36 },
                  px: 0.75,
                  borderRadius: 3,
                  flexShrink: 0,
                  transition: "all 0.15s ease",
                  ...(active && {
                    boxShadow: "0 2px 10px rgba(99, 102, 241, 0.45)",
                  }),
                }}
              />
            );
          })}
        </Box>
      </Box>

      {/* Platform Bonus Gift Panel */}
      <GiftBonusPanel />

      {/* Loading & Error States */}
      {loading && !report && (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="30vh">
          <CircularProgress size={40} />
        </Box>
      )}
      {error && !report && (
        <Alert severity="error" sx={{ mt: 2, mb: 2 }}>{error}</Alert>
      )}
      {!loading && !error && !report && (
        <Alert severity="info" sx={{ mt: 2, mb: 2 }}>No dashboard data available</Alert>
      )}

      {report && (
        <>
          {/* ── KPI Metric Grid (2-Column Mobile First) ── */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "repeat(2, 1fr)",
                sm: "repeat(2, 1fr)",
                md: "repeat(3, 1fr)",
                lg: "repeat(4, 1fr)",
              },
              gap: { xs: 1.25, sm: 2 },
              mb: 3,
            }}
          >
            <StatCard
              title="Total Users"
              value={String(report.statistics?.users?.totalUsers || report.userStats?.totalUsers || 0)}
              sub={`+${report.statistics?.users?.newUsers || report.userStats?.newUsers || report.today?.newUsers || 0} new`}
              gradient="linear-gradient(135deg, #4f46e5, #7c3aed)"
              icon={<People />}
            />
            <StatCard
              title="Total Deposits"
              value={fmt(totalDeposits)}
              sub={`${report.statistics?.deposits?.totalCount || 0} txns`}
              gradient="linear-gradient(135deg, #059669, #10b981)"
              icon={<Payment />}
            />
            <StatCard
              title="Withdrawals"
              value={fmt(totalWithdrawals)}
              sub={`${report.statistics?.withdrawals?.totalCount || 0} txns`}
              gradient="linear-gradient(135deg, #2563eb, #38bdf8)"
              icon={<AccountBalance />}
            />
            <StatCard
              title="Net Cash Flow"
              value={fmt(netFlow)}
              sub={netFlow >= 0 ? "Positive cash flow" : "Negative cash flow"}
              gradient={
                netFlow >= 0
                  ? "linear-gradient(135deg, #0d9488, #10b981)"
                  : "linear-gradient(135deg, #e11d48, #f43f5e)"
              }
              icon={<TrendingUp />}
            />
            {report.houseFeeIncome && (
              <StatCard
                title="House Income"
                value={fmt(report.houseFeeIncome.totalIncome)}
                sub={`Net: ${fmt(report.houseFeeIncome.netIncome)}`}
                gradient="linear-gradient(135deg, #d97706, #f59e0b)"
                icon={<AttachMoney />}
              />
            )}
            <StatCard
              title="Active Rooms"
              value={String(report.statistics?.rooms?.activeRooms || report.gameStats?.activeRooms || 0)}
              sub={`${report.statistics?.rooms?.totalRooms || 0} total rooms`}
              gradient="linear-gradient(135deg, #7c3aed, #c084fc)"
              icon={<MeetingRoom />}
            />
            <StatCard
              title="Tournaments"
              value={String(report.statistics?.tournaments?.totalTournaments || 0)}
              sub={`Rev: ${fmt(report.statistics?.tournaments?.totalRevenue || 0)}`}
              gradient="linear-gradient(135deg, #0284c7, #38bdf8)"
              icon={<Casino />}
            />
            <StatCard
              title="Avg Wallet"
              value={fmt(report.statistics?.users?.averageWalletBalance || 0)}
              sub={`${report.gameStats?.soldCards || 0} cards sold`}
              gradient="linear-gradient(135deg, #334155, #64748b)"
              icon={<AccountBalance />}
            />
          </Box>

          {/* ── Financial Overview Charts ── */}
          <SectionTitle>Financial Overview</SectionTitle>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: txTypeData.length > 0 ? "repeat(2, 1fr)" : "1fr" },
              gap: 2,
              mb: 3,
            }}
          >
            <Card sx={{ borderRadius: { xs: 2.5, sm: 3 } }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Revenue vs Expenses (ETB)
                </Typography>
                <ResponsiveContainer width="100%" height={210}>
                  <BarChart data={financialBarData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis
                      width={38}
                      tick={{ fontSize: 10 }}
                      tickFormatter={(v) => (v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v)}
                    />
                    <Tooltip
                      formatter={(v: any) => [fmt(v), ""]}
                      contentStyle={{ backgroundColor: "#1e1e24", border: "1px solid #333", borderRadius: 8, fontSize: 12 }}
                    />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {financialBarData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {txTypeData.length > 0 && (
              <Card sx={{ borderRadius: { xs: 2.5, sm: 3 } }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Transaction Types
                  </Typography>
                  <ResponsiveContainer width="100%" height={210}>
                    <BarChart data={txTypeData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                      <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                      <YAxis width={35} tick={{ fontSize: 10 }} />
                      <Tooltip contentStyle={{ backgroundColor: "#1e1e24", border: "1px solid #333", borderRadius: 8, fontSize: 12 }} />
                      <Bar dataKey="count" name="Count" fill="#6366f1" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}
          </Box>

          {/* ── Transaction Status & Breakdown ── */}
          <SectionTitle>Status & Distribution</SectionTitle>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: 2,
              mb: 3,
            }}
          >
            {depositStatusData.length > 0 && (
              <Card sx={{ borderRadius: { xs: 2.5, sm: 3 } }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Deposit Status
                  </Typography>
                  <ResponsiveContainer width="100%" height={190}>
                    <PieChart>
                      <Pie
                        data={depositStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={isMobile ? 36 : 46}
                        outerRadius={isMobile ? 58 : 72}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {depositStatusData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v: any) => [`${v} txns`, ""]}
                        contentStyle={{ backgroundColor: "#1e1e24", border: "1px solid #333", borderRadius: 8, fontSize: 12 }}
                      />
                      <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {withdrawalStatusData.length > 0 && (
              <Card sx={{ borderRadius: { xs: 2.5, sm: 3 } }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    Withdrawal Status
                  </Typography>
                  <ResponsiveContainer width="100%" height={190}>
                    <PieChart>
                      <Pie
                        data={withdrawalStatusData}
                        cx="50%"
                        cy="50%"
                        innerRadius={isMobile ? 36 : 46}
                        outerRadius={isMobile ? 58 : 72}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {withdrawalStatusData.map((_, i) => (
                          <Cell key={i} fill={COLORS[(i + 2) % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v: any) => [`${v} txns`, ""]}
                        contentStyle={{ backgroundColor: "#1e1e24", border: "1px solid #333", borderRadius: 8, fontSize: 12 }}
                      />
                      <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* User Stats Vertical Bar */}
            <Card sx={{ borderRadius: { xs: 2.5, sm: 3 } }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  User Distribution
                </Typography>
                <ResponsiveContainer width="100%" height={190}>
                  <BarChart data={userStatsData} layout="vertical" margin={{ top: 0, right: 15, left: -5, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis type="number" tick={{ fontSize: 10 }} />
                    <YAxis type="category" dataKey="name" tick={{ fontSize: 10 }} width={48} />
                    <Tooltip contentStyle={{ backgroundColor: "#1e1e24", border: "1px solid #333", borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="value" radius={[0, 4, 4, 0]}>
                      {userStatsData.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          </Box>

          {/* ── Rooms, Tournaments & Quick Metrics ── */}
          <SectionTitle>Gaming & Platform Activity</SectionTitle>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)", lg: "repeat(3, 1fr)" },
              gap: 2,
              mb: 3,
            }}
          >
            <Card sx={{ borderRadius: { xs: 2.5, sm: 3 } }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Activity Metrics
                </Typography>
                <ResponsiveContainer width="100%" height={190}>
                  <BarChart data={roomTournamentData} margin={{ top: 5, right: 10, left: -15, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
                    <XAxis dataKey="name" tick={{ fontSize: 10 }} />
                    <YAxis width={35} tick={{ fontSize: 10 }} />
                    <Tooltip contentStyle={{ backgroundColor: "#1e1e24", border: "1px solid #333", borderRadius: 8, fontSize: 12 }} />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {roomTournamentData.map((_, i) => (
                        <Cell key={i} fill={COLORS[(i + 1) % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>

            {houseBreakdownData.length > 0 && (
              <Card sx={{ borderRadius: { xs: 2.5, sm: 3 } }}>
                <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                    House Income Breakdown
                  </Typography>
                  <ResponsiveContainer width="100%" height={190}>
                    <PieChart>
                      <Pie
                        data={houseBreakdownData}
                        cx="50%"
                        cy="50%"
                        innerRadius={isMobile ? 36 : 46}
                        outerRadius={isMobile ? 58 : 72}
                        paddingAngle={3}
                        dataKey="value"
                      >
                        {houseBreakdownData.map((_, i) => (
                          <Cell key={i} fill={COLORS[i % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        formatter={(v: any) => [fmt(v), ""]}
                        contentStyle={{ backgroundColor: "#1e1e24", border: "1px solid #333", borderRadius: 8, fontSize: 12 }}
                      />
                      <Legend iconSize={8} wrapperStyle={{ fontSize: 11 }} />
                    </PieChart>
                  </ResponsiveContainer>
                </CardContent>
              </Card>
            )}

            {/* Quick Stats Card */}
            <Card sx={{ borderRadius: { xs: 2.5, sm: 3 } }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                  Key Metrics
                </Typography>
                {[
                  { label: "Cards Sold", value: report.gameStats?.soldCards || 0, color: "primary.main" },
                  { label: "Total Games", value: report.gameStats?.totalGames || 0, color: "info.main" },
                  { label: "Commission Paid", value: fmt(report.commissionStats?.totalCommissionPaid || 0), color: "success.main" },
                  { label: "Referrals Accepted", value: report.referralStats?.byStatus?.accepted?.count || 0, color: "warning.main" },
                  { label: "Avg User Balance", value: fmt(report.statistics?.users?.averageWalletBalance || 0), color: "secondary.main" },
                ].map((s) => (
                  <Box
                    key={s.label}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      py: 0.8,
                      borderBottom: "1px solid rgba(255,255,255,0.06)",
                    }}
                  >
                    <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.8rem" }}>
                      {s.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, color: s.color, fontSize: "0.85rem" }}>
                      {s.value}
                    </Typography>
                  </Box>
                ))}
              </CardContent>
            </Card>
          </Box>

          {/* ── Top Performers (Mobile Leaderboards) ── */}
          <SectionTitle>Top Performers</SectionTitle>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
              gap: 2,
              mb: 3,
            }}
          >
            {/* Top by Balance */}
            <Card sx={{ borderRadius: { xs: 2.5, sm: 3 } }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Top Users by Balance
                </Typography>
                <List dense disablePadding>
                  {(report.userStats?.topUsersByBalance || []).slice(0, 5).map((u: any, i: number) => (
                    <ListItem key={u.userId || i} disablePadding sx={{ py: 0.6 }}>
                      <ListItemAvatar sx={{ minWidth: 38 }}>
                        <Avatar
                          sx={{
                            bgcolor: RANK_COLORS[i % RANK_COLORS.length],
                            width: 28,
                            height: 28,
                            fontSize: 12,
                            fontWeight: 800,
                          }}
                        >
                          {i + 1}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={u.username}
                        primaryTypographyProps={{ variant: "body2", fontWeight: 700, fontSize: "0.82rem" }}
                        secondary={u.telegramUsername || u.userId?.slice(-6) || "—"}
                        secondaryTypographyProps={{ variant: "caption", fontSize: "0.7rem" }}
                      />
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 800, fontSize: "0.85rem" }}>
                        {fmt(u.wallet)}
                      </Typography>
                    </ListItem>
                  ))}
                  {!(report.userStats?.topUsersByBalance?.length) && (
                    <Typography variant="caption" color="text.secondary">No balance records</Typography>
                  )}
                </List>
              </CardContent>
            </Card>

            {/* Most Active */}
            <Card sx={{ borderRadius: { xs: 2.5, sm: 3 } }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Most Active Users
                </Typography>
                <List dense disablePadding>
                  {(report.transactionStats?.mostActiveUsers || []).slice(0, 5).map((u: any, i: number) => (
                    <ListItem key={u.userId || i} disablePadding sx={{ py: 0.6 }}>
                      <ListItemAvatar sx={{ minWidth: 38 }}>
                        <Avatar
                          sx={{
                            bgcolor: RANK_COLORS[i % RANK_COLORS.length],
                            width: 28,
                            height: 28,
                            fontSize: 12,
                            fontWeight: 800,
                          }}
                        >
                          {i + 1}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={u.username}
                        primaryTypographyProps={{ variant: "body2", fontWeight: 700, fontSize: "0.82rem" }}
                        secondary={u.telegramUsername || "—"}
                        secondaryTypographyProps={{ variant: "caption", fontSize: "0.7rem" }}
                      />
                      <Chip
                        label={`${u.transactionCount} txns`}
                        size="small"
                        color="info"
                        sx={{ fontSize: "0.68rem", height: 20 }}
                      />
                    </ListItem>
                  ))}
                  {!(report.transactionStats?.mostActiveUsers?.length) && (
                    <Typography variant="caption" color="text.secondary">No active users</Typography>
                  )}
                </List>
              </CardContent>
            </Card>

            {/* Top Commission Earners */}
            <Card sx={{ borderRadius: { xs: 2.5, sm: 3 } }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                  Top Commission Earners
                </Typography>
                <List dense disablePadding>
                  {(report.commissionStats?.topEarners || []).slice(0, 5).map((e: any, i: number) => (
                    <ListItem key={e._id || i} disablePadding sx={{ py: 0.6 }}>
                      <ListItemAvatar sx={{ minWidth: 38 }}>
                        <Avatar
                          sx={{
                            bgcolor: RANK_COLORS[i % RANK_COLORS.length],
                            width: 28,
                            height: 28,
                            fontSize: 12,
                            fontWeight: 800,
                          }}
                        >
                          {i + 1}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={e.username}
                        primaryTypographyProps={{ variant: "body2", fontWeight: 700, fontSize: "0.82rem" }}
                        secondary={e.telegramUsername || e.phoneNumber || "—"}
                        secondaryTypographyProps={{ variant: "caption", fontSize: "0.7rem" }}
                      />
                      <Typography variant="body2" color="success.main" sx={{ fontWeight: 800, fontSize: "0.85rem" }}>
                        {fmt(e.totalCommission)}
                      </Typography>
                    </ListItem>
                  ))}
                  {!(report.commissionStats?.topEarners?.length) && (
                    <Typography variant="caption" color="text.secondary">No earners recorded</Typography>
                  )}
                </List>
              </CardContent>
            </Card>
          </Box>

          {/* ── Recent Activity Feeds ── */}
          {report.recentActivities && (
            <>
              <SectionTitle>Recent Activity</SectionTitle>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "1fr", md: "repeat(2, 1fr)" },
                  gap: 2,
                  mb: 2,
                }}
              >
                {/* Recent Deposits */}
                <Card sx={{ borderRadius: { xs: 2.5, sm: 3 } }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Recent Deposits
                    </Typography>
                    <List dense disablePadding>
                      {(report.recentActivities.deposits || []).slice(0, 5).map((d: any, i: number) => (
                        <React.Fragment key={d.id || i}>
                          <ListItem disablePadding sx={{ py: 0.8 }}>
                            <ListItemText
                              primary={
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.82rem" }}>
                                    {d.user?.username || d.phoneNumber || "User"}
                                  </Typography>
                                  <Typography variant="body2" color="success.main" sx={{ fontWeight: 800, fontSize: "0.85rem" }}>
                                    +{fmt(d.amount)}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.25}>
                                  <Chip
                                    label={d.status}
                                    size="small"
                                    color={d.status === "completed" ? "success" : d.status === "pending" ? "warning" : "error"}
                                    sx={{ fontSize: "0.65rem", height: 18, textTransform: "capitalize" }}
                                  />
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                                    {dayjs(d.createdAt).format("MMM DD, HH:mm")}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                          {i < 4 && <Divider sx={{ opacity: 0.08 }} />}
                        </React.Fragment>
                      ))}
                      {!(report.recentActivities.deposits?.length) && (
                        <Typography variant="caption" color="text.secondary">No recent deposits</Typography>
                      )}
                    </List>
                  </CardContent>
                </Card>

                {/* Recent Withdrawals */}
                <Card sx={{ borderRadius: { xs: 2.5, sm: 3 } }}>
                  <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
                      Recent Withdrawals
                    </Typography>
                    <List dense disablePadding>
                      {(report.recentActivities.withdrawals || []).slice(0, 5).map((w: any, i: number) => (
                        <React.Fragment key={w.id || i}>
                          <ListItem disablePadding sx={{ py: 0.8 }}>
                            <ListItemText
                              primary={
                                <Box display="flex" justifyContent="space-between" alignItems="center">
                                  <Typography variant="body2" sx={{ fontWeight: 700, fontSize: "0.82rem" }}>
                                    {w.user?.username || "User"}
                                  </Typography>
                                  <Typography variant="body2" color="error.main" sx={{ fontWeight: 800, fontSize: "0.85rem" }}>
                                    -{fmt(w.amount)}
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box display="flex" justifyContent="space-between" alignItems="center" mt={0.25}>
                                  <Chip
                                    label={w.status}
                                    size="small"
                                    color={w.status === "approved" || w.status === "completed" ? "success" : w.status === "pending" ? "warning" : "error"}
                                    sx={{ fontSize: "0.65rem", height: 18, textTransform: "capitalize" }}
                                  />
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: "0.68rem" }}>
                                    {dayjs(w.createdAt).format("MMM DD, HH:mm")}
                                  </Typography>
                                </Box>
                              }
                            />
                          </ListItem>
                          {i < 4 && <Divider sx={{ opacity: 0.08 }} />}
                        </React.Fragment>
                      ))}
                      {!(report.recentActivities.withdrawals?.length) && (
                        <Typography variant="caption" color="text.secondary">No recent withdrawals</Typography>
                      )}
                    </List>
                  </CardContent>
                </Card>
              </Box>
            </>
          )}
        </>
      )}
    </Box>
  );
};

export default DashboardPage;
